import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import ConfirmationDialog from '@components/common/ConfirmationDialog';
import EstimationDialog from '@components/EstimationDialog';
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { CORE_LENSES } from '@constants/analysisLenses';
import { useMixpanel } from '@context/MixpanelProvider';
import { useFetchNextAnalysis, useSingleInterview } from '@hooks/getQueries';
import { useAnalyzeInterview, useEstimateCredits } from '@hooks/postQueries';
import { useCheckCredits } from '@hooks/useCheckCredits';
import usePermission from '@hooks/usePermission';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CodeIcon from '@mui/icons-material/Code'; // Example Icon
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import InterviewCodebook from '@pages/Interview/details/codebook';
import InterviewOverallAnalysis from '@pages/Interview/details/overall-analysis';
import InterviewThemes from '@pages/Interview/details/themes';
import { ANALYSIS_GROUNDED_EMERGENCE_EVENTS } from '@utils/eventSchema';
import { useState } from 'react';
import { useParams } from 'react-router';

// Check if grounded emergence is an enterprise feature (disabled for regular users)
const groundedEmergenceLens = CORE_LENSES.find((lens) => lens.id === 'grounded_emergence');
const isEnterpriseFeature = groundedEmergenceLens?.enterprise === true;

const GroundedEmergenceAnalysis = ({ item }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { id } = useParams();
  const [openEstimateDialog, setOpenEstimateDialog] = useState(false);
  const { mutate: analyzeInterview } = useAnalyzeInterview(id);
  const [requiredAmtCredit] = useState(0);
  const { data: interview } = useSingleInterview(id);
  const { data: nextAnalysis } = useFetchNextAnalysis(id);
  const [openConfirmatinoDialog, setOpenConfirmationDialog] = useState(false);
  const { trackEvent } = useMixpanel();

  const { data: permissions } = usePermission({ study: interview });

  const { data: estimateCredits } = useEstimateCredits();

  const [creditsNeeded] = useState(null);

  const { checkCredits, NoCreditDialog } = useCheckCredits(requiredAmtCredit);

  const handleOpenConfirmation = () => {
    setOpenConfirmationDialog(true);
  };
  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    const tabNames = ['codebook', 'themes', 'report'];
    trackEvent('analysis_grounded_emergence', {
      ...ANALYSIS_GROUNDED_EMERGENCE_EVENTS.GROUNDED_EMERGENCE_TAB_CLICKED,
      tab: tabNames[newValue],
    });
  };
  const handleRunAnalysis = () => {
    const selectedOption = 'grounded_emergence';
    // my analysis_level is code_book, theme and report based on the selected tab
    const analysisLevel = activeTab === 0 ? 'code_book' : activeTab === 1 ? 'theme' : 'report';
    // Use the transcripts that were included in this analysis, not all interview transcripts
    const transcriptIds = item?.qualitative_items_included || [];
    // Track analysis start
    trackEvent('analysis_grounded_emergence', {
      ...ANALYSIS_GROUNDED_EMERGENCE_EVENTS.GROUNDED_EMERGENCE_RUN_STARTED,
      lens_type: selectedOption,
      analysis_level: analysisLevel,
      interview_id: id,
      transcript_count: transcriptIds.length,
    });
    // No processId needed - backend uses atomic credit deduction
    checkCredits(
      () =>
        analyzeInterview(
          {
            id,
            analysis_lens_type: selectedOption,
            transcript_ids: transcriptIds,
            analysis_level: analysisLevel,
            // Pass existing analysis_item_id for regenerating themes/report
            analysis_item_id: item?.analysis_item_id,
          },
          {
            onSuccess: () => {
              trackEvent('analysis_grounded_emergence', {
                ...ANALYSIS_GROUNDED_EMERGENCE_EVENTS.GROUNDED_EMERGENCE_RUN_COMPLETED,
                lens_type: selectedOption,
                analysis_level: analysisLevel,
                interview_id: id,
                transcript_count: transcriptIds.length,
              });
              setOpenConfirmationDialog(false);
            },
            onError: () => {
              setOpenConfirmationDialog(false);
            },
          },
        ),
      creditsNeeded,
    );
  };

  if (!item) {
    return <Typography>Loading analysis details...</Typography>;
  }

  const { analysis_item_id, analysis_lens_type, created_at } = item;
  const lensName = analysis_lens_type || 'grounded_emergence'; // Use lens type from item or default

  // Dialog content message based on the selected tab
  // Note: Codebook regeneration is no longer available - users should run a new analysis instead
  const messageContent =
    activeTab === 1
      ? 'Do you want to rerun theme generation? This will regenerate themes based on the current codebook. Please refresh the page after the analysis completes to see the updated results.'
      : 'Are you sure you want to regenerate the report? This will regenerate the report based on the current themes. Please refresh the page after the analysis completes to see the updated results.';

  return (
    <>
      <Card variant="outlined" sx={{ p: 0 }}>
        {/* Hide warning message about changes detected when feature is enterprise-only */}
        {!isEnterpriseFeature && (
          <Box p={1.5}>
            {nextAnalysis?.message && <Alert severity="warning">{nextAnalysis?.message}</Alert>}
          </Box>
        )}
        {/* Show enterprise notice when feature is disabled */}
        {isEnterpriseFeature && (
          <Box p={1.5}>
            <Alert severity="info" icon={<LockOutlinedIcon />}>
              Thematic Analysis requires additional workspace access. Contact service@qualz.ai to
              enable this feature.
            </Alert>
          </Box>
        )}
        <CardHeader
          title={
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
            >
              Grounded Theory Emergence
              <AnalysisLensHelperText lensName={lensName} level="run" size="small" sx={{ ml: 1 }} />
            </Typography>
          }
          subheader={`Analysis Date: ${new Date(created_at).toLocaleDateString()} | ID: ${analysis_item_id}`}
          avatar={<CodeIcon color="primary" />}
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          {/* Tab Navigation */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => handleTabChange(newValue)}
              aria-label="Grounded Analysis Tabs"
            >
              <Tab label="Codebook" id="grounded-tab-0" aria-controls="grounded-tabpanel-0" />
              <Tab label="Themes" id="grounded-tab-1" aria-controls="grounded-tabpanel-1" />
              <Tab label="Report" id="grounded-tab-2" aria-controls="grounded-tabpanel-2" />
            </Tabs>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Only show Re-Generate button for Themes and Report tabs (not Codebook) */}
              {/* Codebook regeneration is no longer available - users should run a new analysis instead */}
              {!isEnterpriseFeature && activeTab !== 0 && (
                <Button
                  variant="contained"
                  sx={{ minWidth: 160 }}
                  onClick={handleOpenConfirmation}
                  startIcon={<BarChartOutlinedIcon />}
                >
                  {`Re-Generate ${activeTab === 1 ? 'Themes' : 'Report'}`}
                </Button>
              )}
            </Box>
          </Box>

          {/* Tab Panels */}
          <Box>
            <div
              role="tabpanel"
              hidden={activeTab !== 0}
              id="grounded-tabpanel-0"
              aria-labelledby="grounded-tab-0"
            >
              {activeTab === 0 && (
                <InterviewCodebook canEdit={permissions?.studyPermission?.edit || false} />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={activeTab !== 1}
              id="grounded-tabpanel-1"
              aria-labelledby="grounded-tab-1"
            >
              {activeTab === 1 && (
                <InterviewThemes canEdit={permissions?.studyPermission?.edit || false} />
              )}
            </div>
            <div
              role="tabpanel"
              hidden={activeTab !== 2}
              id="grounded-tabpanel-2"
              aria-labelledby="grounded-tab-2"
            >
              {/* Pass the item data to the generic report if needed */}
              {activeTab === 2 && (
                <InterviewOverallAnalysis
                  item={item}
                  canEdit={permissions?.studyPermission?.edit || false}
                />
              )}
            </div>
          </Box>
        </CardContent>
      </Card>
      <IncludedTranscripts item={item} />
      <NoCreditDialog />
      <ConfirmationDialog
        open={openConfirmatinoDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        onConfirm={handleRunAnalysis}
        title={`Re-Generate ${activeTab === 1 ? 'Themes' : 'Report'}`}
        content={messageContent}
      />
      <EstimationDialog
        open={openEstimateDialog}
        onClose={() => setOpenEstimateDialog(false)}
        onAgree={handleRunAnalysis}
        description="This will incur additional entitlements."
        title={'This action requires additional entitlements.'}
        estimateCredits={estimateCredits}
      />
    </>
  );
};

export default GroundedEmergenceAnalysis;
